import { FileId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { ClientTransferSubject } from "components/clients/transfers/transfer-types";
import { trpc } from "components/rpc/trpc";
import { CrmTableColumn } from "components/table/types";
import { Badge } from "components/ui/badge";
import { fancyAlert } from "components/ui/fancy-alert";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import * as React from "react";
import { stableQueryOptionsRpc } from "util/requests";

export type TransferredFiles = inferProcedureOutput<
    AppRouter["clientTransfer"]["transferredFiles"]
>;

export interface FileTransferRow {
    fileId: FileId;
    transferState: FileTransferState;
}

export type FileTransferState = "transferred" | "notTransferred";

export const fileTransferState = (
    transferred: TransferredFiles | null,
    fileId: FileId,
): FileTransferState =>
    transferred?.transferredFileIds.includes(fileId) ? "transferred" : "notTransferred";

export const useTransferredFiles = (
    subject: ClientTransferSubject | null,
): TransferredFiles | null => {
    const trpcContext = trpc.useTRPC();
    const query = useQuery({
        ...trpcContext.clientTransfer.transferredFiles.queryOptions(
            subject ? { subject } : skipToken,
        ),
        ...stableQueryOptionsRpc(),
        staleTime: 60 * 1000,
    });
    return query.data ?? null;
};

const TransferFileButton: React.FC<{
    transferred: TransferredFiles;
    subject: ClientTransferSubject;
    fileId: FileId;
}> = ({ transferred, subject, fileId }) => {
    const trpcContext = trpc.useTRPC();
    const queryClient = useQueryClient();
    const transfer = useMutation(
        trpcContext.clientTransfer.transferFile.mutationOptions(),
    );
    const handleClick = React.useCallback(async () => {
        const confirmed = await fancyConfirm(
            `Send this file to ${transferred.receivingAgencyName}?`,
            "They will receive their own copy of this file.",
            "Yes, send it",
            "No, go back",
        );
        if (!confirmed) {
            return;
        }
        try {
            await transfer.mutateAsync({
                clientTransferRequestId: transferred.clientTransferRequestId,
                fileId,
            });
        } catch {
            await fancyAlert(
                "Unable to transfer file",
                `This file could not be sent to ${transferred.receivingAgencyName}. Try again in a moment.`,
            );
            return;
        }
        await queryClient.invalidateQueries({
            queryKey: trpcContext.clientTransfer.transferredFiles.queryOptions({
                subject,
            }).queryKey,
        });
    }, [transfer, transferred, fileId, queryClient, trpcContext, subject]);

    return (
        <div className="flex items-center gap-2">
            <SecondaryCompactButton
                type="button"
                onClick={handleClick}
                disabled={transfer.isPending}>
                Transfer
            </SecondaryCompactButton>
            {transfer.isPending ? <Spinner /> : null}
        </div>
    );
};

export const FileTransferCell: React.FC<{
    transferred: TransferredFiles;
    subject: ClientTransferSubject;
    row: FileTransferRow;
}> = ({ transferred, subject, row }) =>
    row.transferState === "transferred" ? (
        <Badge type="success" value="Transferred" />
    ) : (
        <TransferFileButton
            transferred={transferred}
            subject={subject}
            fileId={row.fileId}
        />
    );

export const fileTransferColumn = <T extends FileTransferRow>(
    transferred: TransferredFiles,
    subject: ClientTransferSubject,
): CrmTableColumn<T> => ({
    key: "transferState",
    header: `Sent to ${transferred.receivingAgencyName}`,
    renderCell: ({ record }) => (
        <FileTransferCell transferred={transferred} subject={subject} row={record} />
    ),
});
